/**
 * Bench Layout Preview - top-view diagram of the configured bench(es).
 *
 * SOURCE, not a build artifact (unlike hbi-configurator.js) - this file has
 * no imports and shares no code with the engine/UI/adapter, so there's
 * nothing to bundle. Edit directly.
 *
 * Deliberately decoupled from the configurator: it never imports anything
 * from it and never reads its DOM. It only listens for the bubbling
 * "hbi:selectionchange" CustomEvent the configurator dispatches (see
 * ui/configurator-ui.js) whenever selections/quantity/add-on-checked
 * change, with detail { widthFt, lengthFt, quantity, addOnActive,
 * roomWidthFt?, trackLengthInches?, trackQty? } - widthFt/lengthFt already
 * parsed to numbers (or null), so this file needs no config data and makes
 * no Shopify calls of its own. The last three are only ever present for a
 * family with a required companion (TD/TN) - absent everywhere else.
 *
 * v1 simplification (2026-08-24, agreed with Dustin): the gap drawn
 * between multiple units is a flat nominal aisle, not each family's real
 * published CM travel/aisle spec (which actually varies by family - MR and
 * SR have different aisle widths at the same bench width, and ST doesn't
 * roll at all). Precise per-family spacing is a possible future
 * refinement, not this version.
 *
 * Track rendering (TD/TN, 2026-10-07): detail also carries roomWidthFt/
 * trackLengthInches/trackQty when the family has a required companion (see
 * ui/configurator-ui.js). Track LENGTH tracks the room's width dimension,
 * which in THIS file's existing coordinate convention is the vertical
 * stacking axis (each stacked unit occupies one bench-width-tall band) -
 * not the horizontal bench-length axis the rest of the diagram already
 * draws along. So each rail draws vertically, under the benches, at its
 * true length (2026-10-08 - previously one band squeezed to the bench
 * stack's height), with trackQty rails spread along the bench length.
 *
 * Track aisle override (TD/TN, 2026-10-19, per CM/Brian's review feedback):
 * real track benches butt together with zero gap and are built to maximize
 * aisle spacing, unlike ST/SR/MR where the flat nominal gap above is a
 * reasonable stand-in. For a track family only, every bench-to-bench gap is
 * 0 except ONE real aisle (after the first unit), sized by a local
 * aisle-size <input> this file injects into the panel itself - purely
 * visual, same "deliberately decoupled" convention as the rest of this
 * file, never wired into the configurator's real state/pricing/engine.
 * ST/SR/MR are completely unaffected - still the flat nominal gap.
 */
(function () {
  "use strict";

  var PX_PER_FT = 16;
  var NOMINAL_GAP_IN = 24; // 2'-0" flat nominal aisle - see file header
  var DEFAULT_TRACK_AISLE_IN = 36; // 3'-0" default for the track aisle-size input
  var MAX_RENDERED_UNITS = 20; // beyond this, drawn units stop and a note says so - never a silent cap

  function svgEl(tag, attrs) {
    var el = document.createElementNS("http://www.w3.org/2000/svg", tag);
    for (var k in attrs) el.setAttribute(k, attrs[k]);
    return el;
  }

  function formatFtIn(totalInches) {
    var ft = Math.floor(totalInches / 12);
    var inch = totalInches - ft * 12;
    var whole = Math.floor(inch);
    var isHalf = inch - whole >= 0.25 && inch - whole < 0.75;
    var extraFt = inch - whole >= 0.75 ? 1 : 0;
    if (extraFt) {
      ft += 1;
      whole = 0;
    }
    return ft + "'-" + whole + (isHalf ? "½" : "") + "″";
  }

  // Draws into `svg`, returns how many units were actually drawn (capped at
  // MAX_RENDERED_UNITS - the caller decides whether/how to note the rest).
  // `track` is { qty, lengthInches, aisleIn? } | null - only families with a
  // required companion (TD/TN) ever pass a non-null value. `aisleIn` is the
  // user-adjustable real-aisle size (inches); defaults to
  // DEFAULT_TRACK_AISLE_IN when a track is present but doesn't specify one.
  function draw(svg, widthFt, lengthFt, quantity, addOnActive, track) {
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    var shown = Math.max(1, Math.min(quantity, MAX_RENDERED_UNITS));

    var benchLpx = lengthFt * PX_PER_FT;
    var benchWpx = widthFt * PX_PER_FT;
    // Non-track: every gap is the flat nominal aisle, unchanged from before.
    // Track: real benches butt together (0 gap) except ONE reserved real
    // aisle after the first unit, sized by the panel's own aisle-size input.
    var trackAisleIn = track ? (track.aisleIn != null ? track.aisleIn : DEFAULT_TRACK_AISLE_IN) : null;
    var trackAislePx = track ? (trackAisleIn / 12) * PX_PER_FT : 0;
    function gapPxAt(i) {
      // gap between unit i and unit i+1
      if (!track) return (NOMINAL_GAP_IN / 12) * PX_PER_FT;
      return i === 0 ? trackAislePx : 0;
    }
    // Track rendering needs its own band to the left of the existing width
    // bracket - widen that margin only when there's a track to draw, so
    // every non-track family's layout is pixel-identical to before.
    var marginLeft = track ? 68 : 46,
      marginTop = 40,
      marginRight = 66,
      marginBottom = track ? 44 : 14; // room for the two-line track caption

    var totalGapPx = 0;
    for (var g = 0; g < shown - 1; g++) totalGapPx += gapPxAt(g);
    var stackHeight = shown * benchWpx + totalGapPx;
    // Track rails run the room's width at TRUE scale - 19'-9" of track is
    // drawn 19'-9" long, not squeezed to the bench stack's height (the
    // 2026-10-07 version drew one band exactly as tall as the benches,
    // which made a ~20' track look 4' long).
    var trackPx = track && track.lengthInches != null ? (track.lengthInches / 12) * PX_PER_FT : 0;
    var contentH = Math.max(stackHeight, trackPx);
    var vbW = marginLeft + benchLpx + marginRight;
    // Track caption (2026-10-14): drawn from the left edge on two lines and
    // the viewBox widened to fit it - on a short bench the one-line caption
    // ran past the viewBox and was clipped. Width is estimated (monospace,
    // ~0.62em per glyph) rather than measured, so it works before layout.
    var capX = 10,
      capSize = 10,
      capLines = null;
    if (track && track.lengthInches != null) {
      capLines = [
        (track.qty || 1) + " floor-track rails × " + formatFtIn(track.lengthInches) + " each",
        "(room width − 3″)",
      ];
      var capW = capX + Math.max(capLines[0].length, capLines[1].length) * capSize * 0.62 + 8;
      vbW = Math.max(vbW, capW);
    }
    var vbH = marginTop + contentH + marginBottom;
    svg.setAttribute("viewBox", "0 0 " + vbW + " " + vbH);

    var lyTop = marginTop - 16;
    svg.appendChild(svgEl("line", { x1: marginLeft, y1: lyTop, x2: marginLeft + benchLpx, y2: lyTop, stroke: "var(--hbi-diagram-muted)", "stroke-width": 1 }));
    svg.appendChild(svgEl("line", { x1: marginLeft, y1: lyTop - 4, x2: marginLeft, y2: lyTop + 4, stroke: "var(--hbi-diagram-muted)", "stroke-width": 1 }));
    svg.appendChild(svgEl("line", { x1: marginLeft + benchLpx, y1: lyTop - 4, x2: marginLeft + benchLpx, y2: lyTop + 4, stroke: "var(--hbi-diagram-muted)", "stroke-width": 1 }));
    var lenLabel = svgEl("text", { x: marginLeft + benchLpx / 2, y: lyTop - 7, "text-anchor": "middle", "font-size": 10.5, fill: "var(--hbi-diagram-ink)", "font-weight": 600 });
    lenLabel.textContent = formatFtIn(lengthFt * 12);
    svg.appendChild(lenLabel);

    if (track && trackPx > 0) {
      // One rail per CM-table quantity, spread evenly along the bench length
      // (inset from each end). Positions are illustrative - CM sets exact
      // rail spacing at install; the count and length are the real numbers.
      var railQty = Math.max(1, track.qty || 1);
      var railW = 4;
      var inset = Math.min(PX_PER_FT, benchLpx / (2 * railQty));
      for (var r = 0; r < railQty; r++) {
        var rx = railQty === 1
          ? marginLeft + benchLpx / 2
          : marginLeft + inset + (r * (benchLpx - 2 * inset)) / (railQty - 1);
        svg.appendChild(svgEl("rect", { x: rx - railW / 2, y: marginTop, width: railW, height: trackPx, rx: 1, fill: "var(--hbi-diagram-cta)", opacity: 0.55 }));
      }
    }

    var yCursor = marginTop;
    for (var i = 0; i < shown; i++) {
      var y = yCursor;
      svg.appendChild(
        svgEl("rect", { x: marginLeft, y: y, width: benchLpx, height: benchWpx, rx: 3, fill: "var(--hbi-diagram-accent-tint)", stroke: "var(--hbi-diagram-accent)", "stroke-width": 1.3 })
      );

      if (addOnActive) {
        svg.appendChild(svgEl("rect", { x: marginLeft + 3, y: y + 3, width: benchLpx - 6, height: 6, rx: 2, fill: "var(--hbi-diagram-cta)", opacity: 0.9 }));
      }

      var bl = svgEl("text", { x: marginLeft + benchLpx / 2, y: y + benchWpx / 2 + 3, "text-anchor": "middle", "font-size": 9.5, fill: "var(--hbi-diagram-ink)", "font-weight": 600 });
      bl.textContent = "UNIT " + (i + 1);
      svg.appendChild(bl);

      if (i === 0) {
        var xw = marginLeft - 12;
        svg.appendChild(svgEl("line", { x1: xw, y1: y, x2: xw, y2: y + benchWpx, stroke: "var(--hbi-diagram-muted)", "stroke-width": 1 }));
        svg.appendChild(svgEl("line", { x1: xw - 4, y1: y, x2: xw + 4, y2: y, stroke: "var(--hbi-diagram-muted)", "stroke-width": 1 }));
        svg.appendChild(svgEl("line", { x1: xw - 4, y1: y + benchWpx, x2: xw + 4, y2: y + benchWpx, stroke: "var(--hbi-diagram-muted)", "stroke-width": 1 }));
        var wLabel = svgEl("text", {
          x: 0,
          y: 0,
          "text-anchor": "middle",
          "font-size": 10.5,
          fill: "var(--hbi-diagram-ink)",
          "font-weight": 600,
          transform: "translate(" + (xw - 9) + "," + (y + benchWpx / 2) + ") rotate(-90)",
        });
        wLabel.textContent = formatFtIn(widthFt * 12);
        svg.appendChild(wLabel);
      }

      if (i < shown - 1) {
        var thisGapPx = gapPxAt(i);
        if (track) {
          // Only the one real reserved aisle ever has a gap; label it bigger
          // and bolder than the old approx. label so it isn't missable (CM
          // feedback: the previous note was too small to notice).
          if (thisGapPx > 0) {
            var gapY = y + benchWpx + thisGapPx / 2;
            var tal = svgEl("text", {
              x: marginLeft + benchLpx * 0.72,
              y: gapY + 4,
              "text-anchor": "middle",
              "font-size": 11,
              "font-weight": 700,
              fill: "var(--hbi-diagram-cta)",
            });
            tal.textContent = formatFtIn(trackAisleIn) + " aisle";
            svg.appendChild(tal);
          }
          // Remaining units butt together - no gap, no label, by design.
        } else {
          var gapY2 = y + benchWpx + thisGapPx / 2;
          var al = svgEl("text", { x: marginLeft + benchLpx * 0.72, y: gapY2 + 3, "text-anchor": "middle", "font-size": 8.5, fill: "var(--hbi-diagram-muted)" });
          al.textContent = i === 0 ? "2′-0″ aisle (approx.)" : "2′-0″";
          svg.appendChild(al);
        }
        yCursor += benchWpx + thisGapPx;
      }
    }

    if (track && trackPx > 0) {
      // Track-length bracket on the far left (outside the bench-width
      // bracket), spanning the rails' true length, plus a plain caption.
      var tx = 18;
      var tc = "var(--hbi-diagram-cta)";
      svg.appendChild(svgEl("line", { x1: tx, y1: marginTop, x2: tx, y2: marginTop + trackPx, stroke: tc, "stroke-width": 1 }));
      svg.appendChild(svgEl("line", { x1: tx - 4, y1: marginTop, x2: tx + 4, y2: marginTop, stroke: tc, "stroke-width": 1 }));
      svg.appendChild(svgEl("line", { x1: tx - 4, y1: marginTop + trackPx, x2: tx + 4, y2: marginTop + trackPx, stroke: tc, "stroke-width": 1 }));
      var trackLabel = svgEl("text", {
        x: 0,
        y: 0,
        "text-anchor": "middle",
        "font-size": 10.5,
        fill: tc,
        "font-weight": 700,
        transform: "translate(" + (tx - 8) + "," + (marginTop + trackPx / 2) + ") rotate(-90)",
      });
      trackLabel.textContent = formatFtIn(track.lengthInches) + " track";
      svg.appendChild(trackLabel);

      for (var c = 0; c < capLines.length; c++) {
        var cap = svgEl("text", { x: capX, y: marginTop + contentH + 20 + c * 14, "font-size": capSize, fill: tc, "font-weight": c === 0 ? 700 : 400 });
        cap.textContent = capLines[c];
        svg.appendChild(cap);
      }
    }

    if (shown > 1) {
      var xr = marginLeft + benchLpx + 14;
      svg.appendChild(svgEl("line", { x1: xr, y1: marginTop, x2: xr, y2: marginTop + stackHeight, stroke: "var(--hbi-diagram-accent)", "stroke-width": 1 }));
      svg.appendChild(svgEl("line", { x1: xr - 4, y1: marginTop, x2: xr + 4, y2: marginTop, stroke: "var(--hbi-diagram-accent)", "stroke-width": 1 }));
      svg.appendChild(svgEl("line", { x1: xr - 4, y1: marginTop + stackHeight, x2: xr + 4, y2: marginTop + stackHeight, stroke: "var(--hbi-diagram-accent)", "stroke-width": 1 }));
      var totalIn = shown * widthFt * 12 + (track ? (shown > 1 ? trackAisleIn : 0) : (shown - 1) * NOMINAL_GAP_IN);
      var overallLabel = svgEl("text", {
        x: 0,
        y: 0,
        "text-anchor": "middle",
        "font-size": 10.5,
        fill: "var(--hbi-diagram-accent)",
        "font-weight": 700,
        transform: "translate(" + (xr + 12) + "," + (marginTop + stackHeight / 2) + ") rotate(-90)",
      });
      overallLabel.textContent = formatFtIn(totalIn) + " overall";
      svg.appendChild(overallLabel);
    }

    return shown;
  }

  function initDiagram(root) {
    var panel = root.querySelector(".hbi-diagram__panel");
    var svg = root.querySelector(".hbi-diagram__svg");
    var note = root.querySelector(".hbi-diagram__note");
    var svgWrap = root.querySelector(".hbi-diagram__svg-wrap");
    if (!panel || !svg || !note || !svgWrap) return;

    var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Track aisle-size control: injected once, on first track-bearing event,
    // directly above the diagram's own panel. Purely local UI state - never
    // dispatched anywhere, never read by the configurator. Only shown when
    // the current selection actually has a resolved track.
    var aisleIn = DEFAULT_TRACK_AISLE_IN;
    var aisleControl = null;
    var aisleHint = null;
    var lastRawArgs = null; // the RAW args (track with no aisleIn baked in yet), so the aisle input can re-run render() with the current aisleIn

    function ensureAisleHint() {
      if (aisleHint) return aisleHint;
      var hint = document.createElement("p");
      hint.className = "hbi-diagram__aisle-hint";
      hint.textContent = "Ordering more than one? Set quantity to 2+ to open an adjustable aisle between benches.";
      hint.style.cssText = "margin:0;font-size:11.5px;color:var(--hbi-diagram-muted);";
      panel.insertBefore(hint, svgWrap);
      aisleHint = hint;
      return hint;
    }

    function ensureAisleControl() {
      if (aisleControl) return aisleControl;
      var wrap = document.createElement("div");
      wrap.className = "hbi-diagram__aisle-control";
      wrap.style.cssText = "display:flex;align-items:center;gap:8px;font-size:12.5px;";
      var label = document.createElement("label");
      label.textContent = "Aisle size";
      label.style.cssText = "display:flex;align-items:center;gap:8px;font-weight:600;color:var(--hbi-diagram-ink);";
      var input = document.createElement("input");
      input.type = "number";
      input.min = "0";
      input.step = "1";
      input.value = String(aisleIn);
      input.style.cssText =
        "width:56px;padding:3px 6px;border:1px solid var(--hbi-diagram-muted);border-radius:4px;background:rgba(255,255,255,0.06);color:var(--hbi-diagram-ink);";
      var unit = document.createElement("span");
      unit.textContent = "in";
      unit.style.cssText = "color:var(--hbi-diagram-muted);";
      label.appendChild(input);
      wrap.appendChild(label);
      wrap.appendChild(unit);
      input.addEventListener("input", function () {
        var v = parseFloat(input.value);
        if (!isFinite(v) || v < 0) return;
        aisleIn = v;
        if (lastRawArgs) render.apply(null, lastRawArgs);
      });
      // Placed immediately above the drawn diagram itself (not above the
      // panel's own title) - it's a control for the canvas below it, not a
      // heading-level element.
      panel.insertBefore(wrap, svgWrap);
      aisleControl = wrap;
      return wrap;
    }

    function renderNow(widthFt, lengthFt, quantity, addOnActive, track) {
      var shown = draw(svg, widthFt, lengthFt, quantity, addOnActive, track);
      panel.setAttribute("data-state", "resolved");
      if (quantity > shown) {
        note.textContent = "Showing " + shown + " of " + quantity + " - not drawn past this to keep the diagram readable.";
        note.style.display = "block";
      } else {
        note.textContent = "";
        note.style.display = "none";
      }
    }

    function render(widthFt, lengthFt, quantity, addOnActive, track) {
      lastRawArgs = [widthFt, lengthFt, quantity, addOnActive, track];
      var resolvedTrack = track ? { lengthInches: track.lengthInches, qty: track.qty, aisleIn: aisleIn } : null;
      // The real control only shows once there's a second unit to actually
      // open an aisle between - a single bench has nothing for it to adjust.
      // Below 2, show a hint instead so the capability isn't undiscoverable
      // (Dustin: customers shouldn't have to stumble onto qty 2 to find it).
      if (track && quantity >= 2) {
        ensureAisleControl().style.display = "flex";
        if (aisleHint) aisleHint.style.display = "none";
      } else if (track && quantity === 1) {
        if (aisleControl) aisleControl.style.display = "none";
        ensureAisleHint().style.display = "block";
      } else {
        if (aisleControl) aisleControl.style.display = "none";
        if (aisleHint) aisleHint.style.display = "none";
      }
      renderNow(widthFt, lengthFt, quantity, addOnActive, resolvedTrack);
    }

    document.addEventListener("hbi:selectionchange", function (e) {
      var d = e.detail || {};
      if (d.widthFt == null || d.lengthFt == null) {
        panel.setAttribute("data-state", "empty");
        note.style.display = "none";
        return;
      }
      var quantity = Math.max(1, d.quantity || 1);
      var addOnActive = !!d.addOnActive;
      // Required companion (TD/TN): only draw a track band once both its
      // length and quantity have actually resolved - a half-known track
      // (e.g. room width entered but bench length not yet picked) draws no
      // band at all rather than a misleading partial one.
      var track =
        d.trackLengthInches != null && d.trackQty != null
          ? { lengthInches: d.trackLengthInches, qty: d.trackQty }
          : null;

      if (reduceMotion) {
        render(d.widthFt, d.lengthFt, quantity, addOnActive, track);
      } else {
        svg.style.opacity = 0;
        requestAnimationFrame(function () {
          render(d.widthFt, d.lengthFt, quantity, addOnActive, track);
          svg.style.opacity = 1;
        });
      }
    });
  }

  document.querySelectorAll("[data-hbi-bench-diagram]").forEach(initDiagram);
})();
